import React, { type FC, useState } from 'react';
import { injectIntl, type WithIntlProps, type WrappedComponentProps } from 'react-intl';

import Button from '@atlaskit/button/default/button';
import IconButton from '@atlaskit/button/icon/button';
import { cssMap } from '@atlaskit/css';
import Heading from '@atlaskit/heading/heading';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import { token } from '@atlaskit/tokens';
import Textfield from '@atlaskit/textfield/text-field';
import DropdownMenu from '@atlaskit/dropdown-menu/dropdown-menu';
import DropdownItem from '@atlaskit/dropdown-menu/dropdown-menu-item';
import DropdownItemGroup from '@atlaskit/dropdown-menu/dropdown-menu-item-group';
import ArrowLeftIcon from '@atlaskit/icon/core/arrow-left';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';

import { ContentStateColor } from '../../ContentStateColor.tsx';
import { PRIMARY_COLORS } from '../../colors.ts';

import { i18n } from './contentStateMenuV2I18n.ts';

const styles = cssMap({
	dialog: {
		minWidth: '320px',
		maxWidth: '360px',
	},
	addCustomHeader: {
		gap: token('space.100'),
		alignItems: 'center',
		marginBlockEnd: token('space.200'),
	},
	addCustomForm: {
		gap: token('space.150'),
	},
	colorAndName: {
		gap: token('space.100'),
	},
	formButtons: {
		gap: token('space.100'),
		justifyContent: 'flex-end',
	},
	addCustomContainer: {
		paddingInline: token('space.200'),
		paddingBlock: token('space.200'),
	},
	colorButtonContent: {
		height: '20px',
	},
	colorPickerButton: {
		borderColor: token('color.border.input'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderRadius: token('radius.small'),
		backgroundColor: token('color.background.input'),
	},
});

const colorsArray = [
	PRIMARY_COLORS.BLUE,
	PRIMARY_COLORS.GREEN,
	PRIMARY_COLORS.YELLOW,
	PRIMARY_COLORS.RED,
	PRIMARY_COLORS.PURPLE,
];

type ContentStateMenuV2CreateViewProps = WrappedComponentProps & {
	initialValue: string;
	onBack: () => void;
	onSave: (statusName: string, colorId: string) => void;
};

const ContentStateMenuV2CreateViewComponent: FC<ContentStateMenuV2CreateViewProps> = ({
	initialValue,
	onBack,
	onSave,
	intl: { formatMessage },
}) => {
	const [statusName, setStatusName] = useState<string>(initialValue);
	const [selectedColorId, setSelectedColorId] = useState<string>(colorsArray[0] || '');

	const handleSave = () => {
		if (statusName.trim()) {
			onSave(statusName.trim(), selectedColorId);
		}
	};

	return (
		<Stack xcss={styles.dialog} testId="add-custom-status-view">
			<Box xcss={styles.addCustomContainer}>
				<Inline xcss={styles.addCustomHeader}>
					<IconButton
						icon={ArrowLeftIcon}
						label="Back"
						spacing="compact"
						onClick={onBack}
						testId="back-to-search-button"
					/>
					<Heading as="h2" size="xsmall">
						{formatMessage(i18n.addCustomStatusHeader)}
					</Heading>
				</Inline>
				<Stack xcss={styles.addCustomForm}>
					<Inline xcss={styles.colorAndName}>
						<DropdownMenu<HTMLButtonElement>
							trigger={({ triggerRef, ...triggerProps }) => (
								<Box xcss={styles.colorPickerButton}>
									<Button
										{...triggerProps}
										ref={triggerRef}
										appearance="subtle"
										iconAfter={() => <ChevronDownIcon label="" size="small" />}
										aria-label={formatMessage(i18n.colorPickerLabel)}
										testId="color-picker-trigger"
									>
										<Inline alignBlock="center" xcss={styles.colorButtonContent}>
											<ContentStateColor color={selectedColorId} appearance="contentStateButton" />
										</Inline>
									</Button>
								</Box>
							)}
							placement="bottom-start"
						>
							<DropdownItemGroup>
								{colorsArray.map((color, index) => (
									<DropdownItem
										key={color}
										onClick={() => setSelectedColorId(color || '')}
										testId={`color-option-${index}`}
										aria-label={formatMessage(i18n.colorOption)}
									>
										<Inline xcss={styles.colorAndName}>
											<ContentStateColor color={color} appearance="contentStateButton" />
										</Inline>
									</DropdownItem>
								))}
							</DropdownItemGroup>
						</DropdownMenu>
						<Textfield
							value={statusName}
							onChange={(e) => setStatusName(e.currentTarget.value)}
							placeholder={formatMessage(i18n.statusNamePlaceholder)}
							appearance="standard"
							maxLength={20}
							isCompact
							testId="custom-status-name-input"
						/>
					</Inline>
					<Inline xcss={styles.formButtons}>
						<Button appearance="subtle" onClick={onBack} testId="cancel-custom-status-button">
							{formatMessage(i18n.cancel)}
						</Button>
						<Button
							appearance="primary"
							onClick={handleSave}
							isDisabled={!statusName.trim()}
							testId="save-custom-status-button"
						>
							{formatMessage(i18n.save)}
						</Button>
					</Inline>
				</Stack>
			</Box>
		</Stack>
	);
};

export const ContentStateMenuV2CreateView: React.FC<
	WithIntlProps<ContentStateMenuV2CreateViewProps>
> & {
	WrappedComponent: React.ComponentType<ContentStateMenuV2CreateViewProps>;
} = injectIntl(ContentStateMenuV2CreateViewComponent);
